import { create } from 'zustand'
import { FeatureFlagKey, featureFlagKeys, defaultFeatureFlags, FeatureFlags } from '../launchdarkly/types';
import { sleep } from '../utils';
import { useAccountMatterStore } from './use-account-matter-store';

const STORAGE_KEY = 'feature-flag-overrides'
const SERVER_FLAGS_STORAGE_KEY = 'feature-flag-server-values'

// Helper to safely access localStorage
const getLocalStorageItem = (key: string): string | null => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(key)
  }
  return null
}

const setLocalStorageItem = (key: string, value: string): void => {
  if (typeof window !== 'undefined') {
    localStorage.setItem(key, value)
  }
}
interface FeatureFlagsStore extends FeatureFlags {
  featuresLoading: boolean
  featuresErrorMessage: string
  // so this is to keep compatibility as the set feature stuff is a bit "distributed"
  volatileSetFeature: (key: FeatureFlagKey, value: FeatureFlags[typeof key]) => void;
}

const getOverriddenFeatures = (baseFeatures: FeatureFlags) => {
  // First check query params
  let overrides = {} as FeatureFlags
  // Check localStorage for overrides
  const savedOverrides = getLocalStorageItem(STORAGE_KEY)
  if (savedOverrides) {
    overrides = JSON.parse(savedOverrides)
  }

  // Apply overrides to base features
  const finalFeatures: FeatureFlags = featureFlagKeys.reduce((acc, key) => {
    acc[key] = (overrides[key] ?? baseFeatures[key]) as any
    return acc
  }, {} as Record<FeatureFlagKey, any>)

  return finalFeatures
}

const getInitialFlags = () => {
  let flags = JSON.parse(JSON.stringify(defaultFeatureFlags));

  const savedServerFlags = getLocalStorageItem(SERVER_FLAGS_STORAGE_KEY)
  if (savedServerFlags) {
    const parsedFlags = JSON.parse(savedServerFlags)
    flags = { ...flags, ...parsedFlags }
  }

  // Apply any existing overrides on top of saved server flags
  return getOverriddenFeatures(flags)
}

export const useFeatureFlagsStore = create<FeatureFlagsStore>((set) => ({
  ...getInitialFlags(),
  featuresLoading: true,
  featuresErrorMessage: '',
  volatileSetFeature: (key, value) => {
    if (!featureFlagKeys.includes(key)) throw new Error(`cannot set unknown feature: ${key}`)
    set({ [key]: !!value })
  },
}));

const fetchDataWithAbort = async (signal: AbortSignal) => {
  const { accountId } = useAccountMatterStore.getState();
  if (!accountId) {
    useFeatureFlagsStore.setState({ featuresLoading: false, featuresErrorMessage: '' });
    return
  }

  useFeatureFlagsStore.setState({ featuresLoading: true, featuresErrorMessage: '' });

  try {
    const maxAttempts = 10
    let attempt = 0
    while (true) {
      if (signal.aborted) {
        console.log('Feature flags fetch aborted')
        return
      }

      const response = await fetch(`/api/features`, {
        headers: {
          'Accept': 'application/json'
        }
      })

      // we don't pass signal to fetch as it creates too many errors in server logs, but exit after fetch if aborted
      if (signal.aborted) {
        return
      }

      if (response.status === 401) {
        if (attempt >= maxAttempts) {
          throw new Error('Unauthorized from features')
        }
        console.warn('Unauthorized from features, sleeping and retrying')
        attempt += 1
        await sleep(3000)
        continue
      }
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const responseText = await response.text()

      let features
      try {
        features = JSON.parse(responseText) as {
          key: FeatureFlagKey | undefined
          value: string | undefined
        }[]
      } catch (err) {
        console.error('Failed to parse feature flags response:', err)
        throw new Error('Invalid JSON in feature flags response')
      }

      const baseFeatures = Object.fromEntries(features.map(({ key, value }) => [key, value])) as FeatureFlags

      // Apply any feature overrides from query parameters or localStorage
      setLocalStorageItem(SERVER_FLAGS_STORAGE_KEY, JSON.stringify(baseFeatures))
      useFeatureFlagsStore.setState(getOverriddenFeatures(baseFeatures as any));
      break
    }
  } catch (err: any) {
    if (err?.name !== 'AbortError') {
      console.error('Error fetching feature flags:', err)
      useFeatureFlagsStore.setState({ featuresErrorMessage: err.message });
    }
  } finally {
    if (!signal.aborted) {
      useFeatureFlagsStore.setState({ featuresLoading: false });
    }
  }
}

let currentController: AbortController | null = null;

const fetchData = () => {
  // need to keep this in closure so we can compare after finishing.
  const closureController = new AbortController();
  if (currentController) {
    currentController.abort();
  }
  currentController = closureController;

  fetchDataWithAbort(closureController.signal).catch((err) => {
    if (err?.name !== 'AbortError') {
      console.error('Error fetching feature flags:', err);
    }
  }).finally(() => {
    if (!closureController.signal.aborted && closureController === currentController) {
      currentController = null;
    }
  })
}

// we need to subscribe to the useAccountMatterStore to see accountId changes
const subscribeToAccountId = () => {
  const unsubscribe = useAccountMatterStore.subscribe(
    (state, prevState) => {
      if (state.accountId !== prevState.accountId) {
        fetchData();
      }
    }
  );

  // Return cleanup function
  return () => {
    // Abort any ongoing request
    if (currentController) {
      currentController.abort();
      currentController = null;
    }
    // Unsubscribe from the store
    unsubscribe();
  };
}

export const initFeatureFlagsStore = () => {
  const unsubFromAccountId = subscribeToAccountId();
  fetchData();

  return [unsubFromAccountId];
}

